import { Link, useLocation, useParams } from "react-router-dom";
import type { ReactNode } from "react";
import {
  ArrowRight,
  Bot,
  Building2,
  FileCode2,
  FileDown,
  Inbox,
  Info,
  Mail,
  SearchX,
  Send,
  ShoppingBag,
  Wallet,
} from "lucide-react";
import { FaqSection } from "@/components/landing/FaqSection";
import { GpcConverter } from "@/components/guides/GpcConverter";
import { IsdocViewer } from "@/components/guides/IsdocViewer";
import { QrPaymentGenerator } from "@/components/guides/QrPaymentGenerator";
import { AccountIbanConverter } from "@/components/guides/AccountIbanConverter";
import { GpcToPohodaConverter } from "@/components/guides/GpcToPohodaConverter";
import { PohodaHowTo } from "@/components/guides/PohodaHowTo";
import { GuideClosing } from "@/components/guides/GuideClosing";
import { GuideDiagram, GuideVisual } from "@/components/guides/GuideVisuals";
import { LandingNav } from "@/components/landing/LandingNav";
import {
  Container,
  type ContourVariant,
  ContourField,
  CtaAnchor,
  CtaLink,
  Eyebrow,
  Section,
  SectionHeading,
} from "@/components/landing/primitives";
import { formatGuideDate, getGuide, guideNavPath } from "@/lib/guides";
import type { GuideIcon, GuideKey } from "@/lib/guides/types";
import { cn } from "@/lib/utils";
import {
  isLandingLocale,
  type LandingLocale,
  type Localized,
  localePath,
  makeTr,
  pick,
} from "@/lib/landing/locale";
import { czPlural, PLANS } from "@/lib/pricing/plans";
import { languageFromPath } from "@/lib/site/publicPages";

/**
 * One guide page ("průvodce"): a job Zettl does, answered first, then how it
 * works, honest limits, the page's own FAQ (FAQPage JSON-LD is built from the
 * same entries) and links to the neighbouring guides.
 *
 * Prerendered like the landing page: the language comes from the URL, and
 * nothing reads window, document or storage during render.
 */
/** `2026-11-03` as "3. 11. 2026", the same in Node and every browser. */
function formatCzDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  return `${d}.\u00a0${m}.\u00a0${y}`;
}

const ICONS: Record<GuideIcon, ReactNode> = {
  inbox: <Inbox className="size-[18px]" />,
  missing: <SearchX className="size-[18px]" />,
  mail: <Mail className="size-[18px]" />,
  send: <Send className="size-[18px]" />,
  isdoc: <FileCode2 className="size-[18px]" />,
  shop: <ShoppingBag className="size-[18px]" />,
  payout: <Wallet className="size-[18px]" />,
  companies: <Building2 className="size-[18px]" />,
  assistant: <Bot className="size-[18px]" />,
  export: <FileDown className="size-[18px]" />,
};

/** Grid columns for n step cards, so rows come out even. */
function stepColumns(n: number): string {
  if (n % 4 === 0) return "md:grid-cols-2 lg:grid-cols-4";
  if (n % 3 === 0 || n === 5) return "md:grid-cols-2 lg:grid-cols-3";
  return "md:grid-cols-2";
}

const ICON_TILE =
  "flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-[hsl(var(--action))] dark:text-primary";

/** The heading over each browser-only tool on its page. */
const BROWSER_TOOLS = {
  gpcToCsv: {
    title: {
      cs: "Převést výpis GPC / ABO do CSV nebo Excelu",
      en: "Convert a GPC / ABO statement to CSV or Excel",
    },
  },
  isdocViewer: {
    title: { cs: "Otevřít fakturu ISDOC", en: "Open an ISDOC invoice" },
  },
  qrPayment: {
    title: { cs: "Vytvořit QR platbu", en: "Make a QR payment" },
  },
  accountIban: {
    title: { cs: "Převést číslo účtu nebo IBAN", en: "Convert an account number or IBAN" },
  },
  gpcPohoda: {
    title: { cs: "Převést výpis GPC do POHODY", en: "Convert a GPC statement for POHODA" },
  },
} as const;

/** Contour variants for a guide's plain bands, by texture (primitives.tsx). */
const CONTOURS_B: readonly ContourVariant[] = ["frame", "flank"];
const CONTOURS_A: readonly ContourVariant[] = ["side", "field"];

export default function Guide({ pageKey }: { pageKey: GuideKey }) {
  const { lang } = useParams<{ lang: string }>();
  const { pathname } = useLocation();
  const locale = isLandingLocale(lang) ? lang : (languageFromPath(pathname) ?? "cs");
  const tr = makeTr(locale);
  const guide = getGuide(pageKey);
  const navPath = guideNavPath(pageKey);
  const trialDays = PLANS[0].trialDays;
  const otherLabel =
    guide.comparisonWith?.name ?? tr("Ručně v tabulce", "By hand in a spreadsheet");

  // Bands alternate from the hero (plain) down to the closing, whichever
  // optional sections a guide has: tinted, plain, tinted… Every plain band
  // carries contours, each a different variant, and their two textures keep
  // alternating after the hero's (DESIGN_SYSTEM.md → contours).
  const bands = [
    "steps",
    guide.diagram && "diagram",
    guide.features && "features",
    // The step-by-step POHODA guide brings three bands of its own.
    ...(guide.tool === "pohodaHowTo" ? ["howTo", "howTo2", "howTo3"] : []),
    guide.comparison && "comparison",
    "notes",
    "faq",
    "related",
  ].filter(Boolean) as string[];
  const isTinted = (id: string) => bands.indexOf(id) % 2 === 0;
  const toneOf = (id: string): "tinted" | "plain" => (isTinted(id) ? "tinted" : "plain");
  const plainBands = bands.filter((id) => !isTinted(id));
  const contourOf = (id: string): ContourVariant | undefined => {
    const k = plainBands.indexOf(id);
    if (k < 0) return undefined;
    // After the hero (texture a): b, a, b, a…; the FAQ keeps its own column.
    if (k % 2 === 0) return CONTOURS_B[(k / 2) % CONTOURS_B.length];
    if (id === "faq") return "column";
    return CONTOURS_A[((k - 1) / 2) % CONTOURS_A.length];
  };
  const faqTone = toneOf("faq");
  // The last band runs on into the closing block when it is tinted.
  const relatedTinted = isTinted("related");

  return (
    <div
      lang={locale}
      className="min-h-screen overflow-x-clip bg-background text-foreground antialiased"
    >
      <LandingNav locale={locale} path={navPath} />

      <main id="obsah">
        <section aria-labelledby="guide-title" className="relative isolate overflow-hidden">
          <ContourField variant="hero" className="opacity-[0.14] dark:opacity-[0.08]" />
          <Container
            className={cn(
              "pb-14 pt-14 sm:pb-20 sm:pt-20",
              guide.visual && "grid items-center gap-12 lg:grid-cols-[1.1fr_1fr] lg:gap-14"
            )}
          >
            <div>
              <nav aria-label={tr("Drobečková navigace", "Breadcrumb")} className="mb-6">
                <ol className="flex flex-wrap items-center gap-x-2 text-sm text-muted-foreground">
                  <li>
                    <Link to={localePath(locale, "/")} className="hover:text-foreground">
                      Zettl
                    </Link>
                  </li>
                  <li aria-hidden="true">/</li>
                  <li>
                    <Link
                      to={localePath(locale, guideNavPath(guide.freeTool ? "tools" : "guides"))}
                      className="hover:text-foreground"
                    >
                      {guide.freeTool
                        ? tr("Nástroje zdarma", "Free tools")
                        : tr("Průvodce", "Guides")}
                    </Link>
                  </li>
                </ol>
              </nav>
              <Eyebrow className="mb-3">{pick(guide.eyebrow, locale)}</Eyebrow>
              <h1
                id="guide-title"
                className="max-w-3xl text-[2rem] font-semibold leading-[1.1] tracking-tight sm:text-5xl"
              >
                {pick(guide.h1, locale)}
              </h1>
              <p className="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
                {pick(guide.lead, locale)}
              </p>
              {guide.updated && (
                <p className="mt-3 text-sm text-muted-foreground">
                  {tr("Aktualizováno ", "Updated ")}
                  <time dateTime={guide.updated}>{formatGuideDate(guide.updated, locale)}</time>
                </p>
              )}
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <CtaLink to={localePath(locale, "/auth?tab=register")} arrow>
                  {trialDays
                    ? tr(`Vyzkoušet ${trialDays} dní zdarma`, `Try it free for ${trialDays} days`)
                    : tr("Získat Zettl", "Get Zettl")}
                </CtaLink>
                <CtaLink to={localePath(locale, "/#cenik")} variant="outline">
                  {tr("Ceník", "Pricing")}
                </CtaLink>
              </div>
            </div>
            {guide.visual && (
              <div className="relative">
                <GuideVisual visual={guide.visual} locale={locale} />
              </div>
            )}
          </Container>
        </section>

        {guide.tool && guide.tool !== "pohodaHowTo" && (
          <section
            id={guide.tool === "gpcToCsv" ? "prevod" : "nastroj"}
            aria-labelledby="tool-title"
            className="scroll-mt-28 pb-16 sm:pb-20"
          >
            <Container>
              <div className={guide.tool === "gpcToCsv" ? "max-w-3xl" : "max-w-4xl"}>
                <h2 id="tool-title" className="mb-5 text-xl font-semibold tracking-tight">
                  {pick(BROWSER_TOOLS[guide.tool].title, locale)}
                </h2>
                {guide.tool === "gpcToCsv" && <GpcConverter locale={locale} />}
                {guide.tool === "isdocViewer" && <IsdocViewer locale={locale} />}
                {guide.tool === "qrPayment" && <QrPaymentGenerator locale={locale} />}
                {guide.tool === "accountIban" && <AccountIbanConverter locale={locale} />}
                {guide.tool === "gpcPohoda" && <GpcToPohodaConverter locale={locale} />}
                {guide.freeTool?.bridge && (
                  <ToolBridge bridge={guide.freeTool.bridge} locale={locale} />
                )}
              </div>
            </Container>
          </section>
        )}

        <Section
          id="jak-to-funguje"
          labelledBy="steps-title"
          tone={toneOf("steps")}
          contours={contourOf("steps")}
        >
          <Container>
            <SectionHeading
              id="steps-title"
              eyebrow={tr(
                `${guide.steps.length} ${czPlural(guide.steps.length, "krok", "kroky", "kroků")}`,
                `${guide.steps.length} steps`
              )}
              title={tr("Jak to funguje", "How it works")}
            />
            <ol className={cn("mt-10 grid gap-4", stepColumns(guide.steps.length))}>
              {guide.steps.map((step, i) => (
                <li
                  key={step.title.en}
                  className="relative flex flex-col rounded-2xl border bg-card p-5 sm:p-6"
                >
                  <span
                    aria-hidden="true"
                    className="flex size-10 items-center justify-center rounded-full bg-[hsl(var(--action))] text-sm font-semibold tabular-nums text-[hsl(var(--action-foreground))] dark:bg-primary dark:text-primary-foreground"
                  >
                    {i + 1}
                  </span>
                  <h3 className="mt-4 text-base font-semibold tracking-tight">
                    {pick(step.title, locale)}
                  </h3>
                  <p className="mt-1.5 text-[15px] leading-relaxed text-muted-foreground">
                    {pick(step.text, locale)}
                  </p>
                </li>
              ))}
            </ol>
          </Container>
        </Section>

        {guide.diagram && (
          <Section
            id="schema"
            labelledBy="diagram-title"
            tone={toneOf("diagram")}
            contours={contourOf("diagram")}
          >
            <Container>
              <SectionHeading id="diagram-title" title={pick(guide.diagram.title, locale)} />
              <div className="mt-10">
                <GuideDiagram diagram={guide.diagram.key} locale={locale} />
              </div>
              {guide.deeperLink && (
                <div className="mt-8">
                  <CtaAnchor href={pick(guide.deeperLink.href, locale)}>
                    {pick(guide.deeperLink.label, locale)}
                    <ArrowRight aria-hidden="true" className="ml-2 size-4" />
                  </CtaAnchor>
                </div>
              )}
            </Container>
          </Section>
        )}

        {guide.features && (
          <Section
            id="co-dostanete"
            labelledBy="features-title"
            tone={toneOf("features")}
            contours={contourOf("features")}
          >
            <Container>
              <SectionHeading id="features-title" title={tr("Co dostanete", "What you get")} />
              <ul
                className={cn(
                  "mt-10 grid gap-4 sm:grid-cols-2",
                  guide.features.length % 4 === 0 ? "lg:grid-cols-4" : "lg:grid-cols-3"
                )}
              >
                {guide.features.map((f) => (
                  <li
                    key={f.title.en}
                    className="flex flex-col rounded-2xl border bg-card p-5 sm:p-6"
                  >
                    <span aria-hidden="true" className={ICON_TILE}>
                      {ICONS[f.icon]}
                    </span>
                    <h3 className="mt-4 text-base font-semibold tracking-tight">
                      {pick(f.title, locale)}
                    </h3>
                    <p className="mt-1.5 text-[15px] leading-relaxed text-muted-foreground">
                      {pick(f.text, locale)}
                    </p>
                  </li>
                ))}
              </ul>
            </Container>
          </Section>
        )}

        {guide.tool === "pohodaHowTo" && (
          <PohodaHowTo
            locale={locale}
            startTone={toneOf("howTo")}
            contours={[contourOf("howTo"), contourOf("howTo2"), contourOf("howTo3")]}
          />
        )}

        {guide.comparison && (
          <Section
            id="srovnani"
            labelledBy="comparison-title"
            tone={toneOf("comparison")}
            contours={contourOf("comparison")}
          >
            <Container>
              <SectionHeading
                id="comparison-title"
                title={
                  guide.comparisonWith
                    ? pick(guide.comparisonWith.title, locale)
                    : tr("Ručně, nebo se Zettlem", "By hand, or with Zettl")
                }
              />
              {/* Phones: one card per task; the table from `sm`. */}
              <ul className="mt-10 space-y-3 sm:hidden">
                {guide.comparison.map((row) => (
                  <li key={row.task.en} className="rounded-xl border bg-card p-4 text-sm">
                    <p className="font-medium">{pick(row.task, locale)}</p>
                    <dl className="mt-2 space-y-2">
                      <div>
                        <dt className="text-xs text-muted-foreground">{otherLabel}</dt>
                        <dd className="leading-relaxed text-muted-foreground">
                          {pick(row.byHand, locale)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-muted-foreground">Zettl</dt>
                        <dd className="leading-relaxed">{pick(row.zettl, locale)}</dd>
                      </div>
                    </dl>
                  </li>
                ))}
              </ul>
              <div className="mt-10 hidden overflow-x-auto rounded-xl border bg-card sm:block">
                <table className="w-full min-w-[36rem] text-left text-sm">
                  <thead className="border-b bg-secondary/40 text-xs text-muted-foreground">
                    <tr>
                      <th scope="col" className="px-4 py-3 font-medium">
                        {tr("Úkol", "Task")}
                      </th>
                      <th scope="col" className="px-4 py-3 font-medium">
                        {otherLabel}
                      </th>
                      <th scope="col" className="px-4 py-3 font-medium">
                        Zettl
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {guide.comparison.map((row) => (
                      <tr key={row.task.en} className="align-top">
                        <th scope="row" className="px-4 py-3 font-medium">
                          {pick(row.task, locale)}
                        </th>
                        <td className="px-4 py-3 leading-relaxed text-muted-foreground">
                          {pick(row.byHand, locale)}
                        </td>
                        <td className="px-4 py-3 leading-relaxed">{pick(row.zettl, locale)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Container>
          </Section>
        )}

        <Section
          id="dobre-vedet"
          labelledBy="notes-title"
          tone={toneOf("notes")}
          contours={contourOf("notes")}
        >
          <Container>
            <SectionHeading id="notes-title" title={tr("Dobré vědět", "Good to know")} />
            <ul className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {guide.notes.map((note) => (
                <li
                  key={note.en}
                  className="flex gap-3 rounded-xl border bg-card p-4 text-[15px] leading-relaxed"
                >
                  <Info
                    aria-hidden="true"
                    className="mt-1 size-4 shrink-0 text-[hsl(var(--action))] dark:text-primary"
                  />
                  <span>{pick(note, locale)}</span>
                </li>
              ))}
            </ul>
            {guide.sources && (
              <p className="mt-8 max-w-3xl text-sm leading-relaxed text-muted-foreground">
                {guide.sources.intro
                  ? pick(guide.sources.intro, locale).replace(
                      "{date}",
                      locale === "cs"
                        ? formatCzDate(guide.sources.checkedOn)
                        : guide.sources.checkedOn
                    )
                  : tr(
                      guide.sources.subject
                        ? `Údaje o programu ${guide.sources.subject} jsme ověřili dne ${formatCzDate(guide.sources.checkedOn)} ve veřejné dokumentaci výrobce: `
                        : `Údaje o službě ${guide.comparisonWith?.name ?? ""} jsme ověřili dne ${formatCzDate(guide.sources.checkedOn)} na jejích veřejných stránkách: `,
                      guide.sources.subject
                        ? `We checked the facts about ${guide.sources.subject} on ${guide.sources.checkedOn} in the maker's public documentation: `
                        : `We checked the facts about ${guide.comparisonWith?.name ?? ""} on its public pages on ${guide.sources.checkedOn}: `
                    )}
                {guide.sources.links.map((link, i) => (
                  <span key={link.url}>
                    {i > 0 && ", "}
                    <a
                      href={link.url}
                      rel="nofollow noopener noreferrer"
                      target="_blank"
                      className="underline underline-offset-4 hover:text-foreground"
                    >
                      {link.label}
                    </a>
                  </span>
                ))}
                .
              </p>
            )}
          </Container>
        </Section>

        <FaqSection
          locale={locale}
          tone={faqTone}
          contours={contourOf("faq")}
          items={guide.faq}
          lead={tr("Krátké odpovědi k tomuto tématu.", "Short answers on this topic.")}
        />

        <Section
          id="dalsi"
          labelledBy="related-title"
          tone={relatedTinted ? "tintedToClosing" : "plain"}
          contours={contourOf("related")}
        >
          <Container>
            <SectionHeading id="related-title" title={tr("Co dál číst", "Read next")} />
            <ul className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {guide.related.map((key) => {
                const other = getGuide(key);
                return (
                  <li key={key}>
                    <Link
                      to={localePath(locale, guideNavPath(key))}
                      className="group flex h-full items-start justify-between gap-3 rounded-xl border bg-card p-4 transition-colors hover:bg-secondary/50"
                    >
                      <span>
                        <span className="block font-medium">{pick(other.name, locale)}</span>
                        <span className="mt-1 block text-sm leading-relaxed text-muted-foreground">
                          {pick(other.seo.description, locale)}
                        </span>
                      </span>
                      <ArrowRight
                        aria-hidden="true"
                        className="mt-1 size-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5"
                      />
                    </Link>
                  </li>
                );
              })}
            </ul>
          </Container>
        </Section>
      </main>

      <GuideClosing locale={locale} path={navPath} joined={relatedTinted} />
    </div>
  );
}

/**
 * Under a free tool: the same job done by Zettl every month, with the guide
 * that explains it and the trial. Quiet on purpose: the tool stays the point.
 */
function ToolBridge({
  bridge,
  locale,
}: {
  bridge: { title: Localized; text: Localized; guide: GuideKey };
  locale: LandingLocale;
}) {
  const tr = makeTr(locale);
  const trialDays = PLANS[0].trialDays;
  const target = getGuide(bridge.guide);
  return (
    <aside
      aria-labelledby="tool-bridge-title"
      className="mt-6 rounded-xl border bg-secondary/40 p-5 sm:p-6"
    >
      <h3 id="tool-bridge-title" className="text-base font-semibold tracking-tight">
        {pick(bridge.title, locale)}
      </h3>
      <p className="mt-2 max-w-2xl text-[15px] leading-relaxed text-muted-foreground">
        {pick(bridge.text, locale)}
      </p>
      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <CtaLink to={localePath(locale, "/auth?tab=register")} arrow>
          {trialDays
            ? tr(`Vyzkoušet ${trialDays} dní zdarma`, `Try it free for ${trialDays} days`)
            : tr("Získat Zettl", "Get Zettl")}
        </CtaLink>
        <Link
          to={localePath(locale, guideNavPath(bridge.guide))}
          className="inline-flex items-center gap-1 text-sm font-medium text-[hsl(var(--action))] underline-offset-4 hover:underline dark:text-primary"
        >
          {pick(target.name, locale)}
          <ArrowRight aria-hidden="true" className="size-3.5" />
        </Link>
      </div>
    </aside>
  );
}
